import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Snackbar, Alert, CircularProgress, TextField
} from '@mui/material';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';

const API_URL = process.env.REACT_APP_API_URL;

function CandidateSalaryAdvanceDashboard({ candidateId }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [candidateId]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/candidate/salary-advance/candidate/${candidateId}`);
      const data = await res.json();
      if (data.success) setRequests(data.requests);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load salary advance requests', severity: 'error' });
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || !reason) {
      setSnackbar({ open: true, message: 'Please fill in all fields', severity: 'error' });
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('gogig_token');
      const res = await fetch(`${API_URL}/api/candidate/salary-advance`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          candidate_id: candidateId,
          amount: parseFloat(amount),
          reason
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Request failed');
      setSnackbar({ open: true, message: 'Salary advance request submitted successfully', severity: 'success' });
      setAmount('');
      setReason('');
      fetchRequests();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
    setSubmitting(false);
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={800} mb={3} color="primary.main" textAlign="center">
        💰 Salary Advance
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ bgcolor: '#e8f5e9', boxShadow: 3 }}>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>Request Salary Advance</Typography>
                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <TextField
                      label="Amount"
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      required
                      fullWidth
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
                      }}
                    />
                    <TextField
                      label="Reason"
                      multiline
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      required
                      fullWidth
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={submitting}
                      startIcon={submitting ? <CircularProgress size={20} /> : <LocalAtmIcon />}
                    >
                      {submitting ? 'Submitting...' : 'Submit Request'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ bgcolor: '#fff', boxShadow: 3 }}>
                <CardContent>
                  <Typography variant="h6" color="text.secondary" gutterBottom>Request History</Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Reason</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {requests.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">No requests yet</TableCell>
                          </TableRow>
                        ) : (
                          requests.map((request) => (
                            <TableRow key={request.id}>
                              <TableCell>{new Date(request.created_at).toLocaleDateString()}</TableCell>
                              <TableCell>₹{request.amount}</TableCell>
                              <TableCell>{request.reason}</TableCell>
                              <TableCell>
                                <Typography
                                  sx={{
                                    color: request.status === 'approved' ? 'success.main' :
                                      request.status === 'rejected' ? 'error.main' : 'warning.main',
                                    fontWeight: 600
                                  }}
                                >
                                  {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CandidateSalaryAdvanceDashboard; 